import React from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";

export default function Confidentialite() {
    return (
        <div className="max-w-3xl mx-auto px-6 py-16" data-testid="confidentialite-page">
            <Seo
                title="Politique de confidentialité · DSGVO · Foundation of Light"
                description="Politique de confidentialité RGPD / DSGVO : données traitées, sous-traitants (Brevo, Stripe, PayPal, Zoom, Google), conservation et droits d'accès."
                path="/confidentialite"
                noindex
            />
            <p className="text-xs uppercase tracking-widest text-primary mb-3">Datenschutzerklärung · DSGVO / RGPD</p>
            <h1 className="font-serif text-4xl md:text-5xl mb-6">Politique de confidentialité</h1>
            <p className="text-sm text-muted-foreground mb-12">Dernière mise à jour : août 2026</p>

            <section className="space-y-3 mb-10">
                <h2 className="font-serif text-2xl mb-3">1. Responsable du traitement</h2>
                <p className="text-sm leading-relaxed">
                    Olivier Masurel · Einzelunternehmer<br />
                    Zum Tilmeshof 63, 50859 Cologne, Allemagne<br />
                    Email : <a className="underline decoration-primary/40 underline-offset-4" href="mailto:info@foundation-of-light.com">info@foundation-of-light.com</a> · Tél : +49 1575 6434 770
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                    Le nombre de personnes traitant des données restant inférieur au seuil légal, aucun délégué à la protection des données (DPO) n'est désigné (§ 38 BDSG).
                </p>
            </section>

            <section className="space-y-3 mb-10">
                <h2 className="font-serif text-2xl mb-3">2. Données collectées et finalités</h2>
                <table className="w-full text-sm border-collapse">
                    <thead>
                        <tr className="text-left border-b border-border/60">
                            <th className="py-2">Donnée</th>
                            <th className="py-2">Finalité</th>
                            <th className="py-2">Base légale (art. 6 RGPD)</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border/40">
                        <tr>
                            <td className="py-2 pr-4">Nom, email, mot de passe (hashé)</td>
                            <td className="py-2 pr-4">Création et gestion du compte</td>
                            <td className="py-2">Exécution du contrat · 6.1.b</td>
                        </tr>
                        <tr>
                            <td className="py-2 pr-4">Adresse IP, logs techniques</td>
                            <td className="py-2 pr-4">Sécurité et prévention des abus</td>
                            <td className="py-2">Intérêt légitime · 6.1.f</td>
                        </tr>
                        <tr>
                            <td className="py-2 pr-4">Historique des inscriptions et réservations</td>
                            <td className="py-2 pr-4">Fourniture des prestations</td>
                            <td className="py-2">Exécution du contrat · 6.1.b</td>
                        </tr>
                        <tr>
                            <td className="py-2 pr-4">Données de paiement (traitées par Stripe / PayPal)</td>
                            <td className="py-2 pr-4">Traitement de la commande</td>
                            <td className="py-2">Exécution du contrat · 6.1.b</td>
                        </tr>
                        <tr>
                            <td className="py-2 pr-4">Contributions publiées dans la communauté privée</td>
                            <td className="py-2 pr-4">Fonctionnement de la communauté</td>
                            <td className="py-2">Consentement · 6.1.a</td>
                        </tr>
                        <tr>
                            <td className="py-2 pr-4">Cookie de session (JWT)</td>
                            <td className="py-2 pr-4">Maintien de l'authentification</td>
                            <td className="py-2">Strictement nécessaire · 6.1.f</td>
                        </tr>
                        <tr>
                            <td className="py-2 pr-4">Email (et prénom si fourni) pour l'infolettre</td>
                            <td className="py-2 pr-4">Envoi de l'infolettre (nouveautés, ateliers, transmissions)</td>
                            <td className="py-2">Consentement · 6.1.a</td>
                        </tr>
                    </tbody>
                </table>
                <p className="text-sm text-muted-foreground mt-3">
                    <strong>Aucun cookie de mesure d'audience ou de marketing</strong> n'est déposé. Aucun bandeau de consentement n'est donc requis, seul un bandeau informatif signale le cookie de session essentiel.
                </p>
            </section>

            <section className="space-y-3 mb-10">
                <h2 className="font-serif text-2xl mb-3">3. Sous-traitants et transferts hors UE</h2>
                <p className="text-sm leading-relaxed">
                    Certains sous-traitants sont établis dans l'Union européenne, d'autres en dehors.
                    Pour les transferts hors UE, la relation s'appuie sur les <strong>clauses contractuelles types</strong> de la Commission européenne (Standard Contractual Clauses) ou sur le <strong>EU-U.S. Data Privacy Framework</strong>.
                </p>
                <ul className="text-sm leading-relaxed list-disc pl-6 space-y-1 mt-3">
                    <li><strong>Sendinblue SAS (Brevo)</strong> (France, UE) — envoi des emails transactionnels (confirmations de commande, replays, coordonnées bancaires) et gestion de l'infolettre (contact stocké sur la liste « Newsletter »). Aucune donnée n'est transférée hors UE.</li>
                    <li><strong>Stripe Payments Europe, Ltd.</strong> (Irlande / USA) — traitement des paiements par carte</li>
                    <li><strong>PayPal (Europe) S.à r.l. et Cie, S.C.A.</strong> (Luxembourg / USA) — traitement des paiements PayPal</li>
                    <li><strong>Zoom Video Communications, Inc.</strong> (USA) — visioconférence pour les soins et ateliers</li>
                    <li><strong>Google Ireland Ltd.</strong> (Irlande / USA) — synchronisation de l'agenda (Google Calendar) sur autorisation explicite de l'administrateur uniquement</li>
                    <li><strong>MongoDB, Inc.</strong> (USA) — hébergement de la base de données</li>
                    <li><strong>Emergent</strong> — plateforme d'hébergement du site</li>
                </ul>
            </section>

            <section className="space-y-3 mb-10">
                <h2 className="font-serif text-2xl mb-3">4. Durée de conservation</h2>
                <ul className="text-sm leading-relaxed list-disc pl-6 space-y-1">
                    <li><strong>Compte utilisateur</strong> : pendant toute la durée d'utilisation du service, puis <strong>3 ans</strong> après la dernière activité au titre des obligations comptables allemandes (§ 147 AO).</li>
                    <li><strong>Documents comptables</strong> (factures, transactions) : <strong>10 ans</strong> conformément au § 147 AO.</li>
                    <li><strong>Logs techniques</strong> : 12 mois.</li>
                    <li><strong>Contenus publiés dans la communauté</strong> : jusqu'à suppression du compte ou du contenu par l'utilisateur.</li>
                    <li><strong>Contact newsletter (Brevo)</strong> : jusqu'à désinscription du destinataire (lien de désinscription présent dans chaque email et sur le pied de page du site).</li>
                </ul>
            </section>

            <section className="space-y-3 mb-10">
                <h2 className="font-serif text-2xl mb-3">5. Vos droits</h2>
                <p className="text-sm leading-relaxed">
                    Conformément aux articles 15 à 22 du RGPD, vous disposez à tout moment des droits suivants :
                </p>
                <ul className="text-sm leading-relaxed list-disc pl-6 space-y-1 mt-2">
                    <li>droit d'accès à vos données</li>
                    <li>droit de rectification</li>
                    <li>droit à l'effacement (« droit à l'oubli »)</li>
                    <li>droit à la limitation du traitement</li>
                    <li>droit à la portabilité</li>
                    <li>droit d'opposition</li>
                    <li>droit de retirer votre consentement à tout moment</li>
                </ul>
                <p className="text-sm leading-relaxed mt-3">
                    Pour exercer ces droits, écrivez-nous à{" "}
                    <a className="underline decoration-primary/40 underline-offset-4" href="mailto:info@foundation-of-light.com">info@foundation-of-light.com</a>.
                    Vous disposez également du droit d'introduire une réclamation auprès d'une autorité de contrôle, par exemple la LDI NRW (autorité de protection des données de Rhénanie-du-Nord-Westphalie) ou la CNIL en France.
                </p>
            </section>

            <section className="space-y-3 mb-10">
                <h2 className="font-serif text-2xl mb-3">6. Sécurité</h2>
                <p className="text-sm leading-relaxed">
                    Le site est intégralement servi en HTTPS. Les mots de passe sont hashés (bcrypt) et jamais stockés en clair.
                    Les paiements sont entièrement délégués à des prestataires certifiés PCI-DSS (Stripe, PayPal) : aucune donnée de carte ne transite ni n'est stockée sur nos serveurs.
                </p>
            </section>

            <section className="space-y-3">
                <h2 className="font-serif text-2xl mb-3">7. Modifications</h2>
                <p className="text-sm leading-relaxed">
                    La présente politique peut être mise à jour pour refléter des évolutions techniques, légales ou fonctionnelles.
                    La version en vigueur est toujours celle publiée à cette adresse.
                </p>
            </section>

            <p className="text-xs text-muted-foreground mt-16">
                Voir aussi : <Link to="/mentions-legales" className="underline decoration-primary/40 underline-offset-4">Mentions légales</Link> · <Link to="/cgv" className="underline decoration-primary/40 underline-offset-4">Conditions générales de vente</Link>
            </p>
        </div>
    );
}
